/**
 * IntroShapes — 15 visually-distinct hero/intro layout primitives.
 *
 * Iter 148 — shape-recipe engine. Each shape is a self-contained mini
 * screenshot using real Unsplash photography, real headlines, and real
 * CTA elements. Selected by `tpl._previewShape` (see templates.js).
 *
 * Diversity guarantee — these aren't color/font variants. Each shape
 * has different:
 *   • Image placement (no image, full-bleed, left split, right split,
 *     diagonal collage, floating card, layered, parallax, multi-image)
 *   • Hierarchy (H1-first, eyebrow-first, image-first, type-first)
 *   • Content alignment (left, center, right, asymmetric)
 *   • Visual emphasis (full-width hero, small accent, big-type focus)
 */
import React from 'react';

const IMG = {
  studio:  'https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?w=900&q=70',
  forest:  'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?w=900&q=70',
  desk:    'https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?w=900&q=70',
  city:    'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=900&q=70',
  brand:   'https://images.unsplash.com/photo-1517292987719-0369a794ec0f?w=900&q=70',
  fashion: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&q=70',
  food:    'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=70',
  nature:  'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&q=70',
};

const CTA = ({ label = 'Get Started', dark = false, sz = 8 }) => (
  <span style={{
    display: 'inline-block',
    background: dark ? '#fff' : '#0E0E23',
    color: dark ? '#0E0E23' : '#fff',
    fontSize: sz, fontWeight: 900,
    padding: '6px 12px', borderRadius: 999,
    letterSpacing: 0.6, textTransform: 'uppercase',
  }}>{label} →</span>
);
const Line = ({ w = '60%', h = 3, color = '#CBD5E1', mt = 4, op = 1 }) =>
  <div style={{ width: w, height: h, background: color, opacity: op, borderRadius: 2, marginTop: mt }} />;

// ── 1. Centered Hero ─────────────────────────────────────────────────
export const CenteredHero = () => (
  <div style={{ position:'relative', width:'100%', height:'100%' }}>
    <img src={IMG.studio} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""/>
    <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg,rgba(0,0,0,0.10),rgba(0,0,0,0.65))'}}/>
    <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',color:'#fff',padding:14,textAlign:'center'}}>
      <div style={{fontSize:7,opacity:0.85,letterSpacing:1.2,textTransform:'uppercase',fontWeight:800}}>Welcome</div>
      <div style={{fontSize:18,fontWeight:900,marginTop:6,lineHeight:1.1,letterSpacing:-0.4}}>Make something<br/>they remember</div>
      <div style={{fontSize:8,marginTop:6,opacity:0.85}}>Crafted brand identity & storytelling</div>
      <div style={{marginTop:10}}><CTA dark /></div>
    </div>
  </div>
);

// ── 2. Split Hero · Image Left ───────────────────────────────────────
export const SplitImageLeft = () => (
  <div style={{display:'flex',width:'100%',height:'100%',background:'#fff'}}>
    <img src={IMG.brand} alt="" style={{width:'48%',height:'100%',objectFit:'cover'}}/>
    <div style={{flex:1,padding:14,display:'flex',flexDirection:'column',justifyContent:'center'}}>
      <div style={{fontSize:6,color:'#8B5CF6',fontWeight:800,letterSpacing:1.2,textTransform:'uppercase'}}>Issue 01</div>
      <div style={{fontSize:14,fontWeight:900,color:'#0E0E23',marginTop:4,lineHeight:1.1}}>Stories worth<br/>telling slowly.</div>
      <Line w="80%" mt={6} />
      <Line w="65%" mt={3} />
      <div style={{marginTop:8}}><CTA label="Read" /></div>
    </div>
  </div>
);

// ── 3. Split Hero · Image Right ──────────────────────────────────────
export const SplitImageRight = () => (
  <div style={{display:'flex',width:'100%',height:'100%',background:'#FFFBF5'}}>
    <div style={{flex:1,padding:14,display:'flex',flexDirection:'column',justifyContent:'center'}}>
      <div style={{fontSize:18,fontWeight:900,color:'#0E0E23',lineHeight:1.05,letterSpacing:-0.5}}>Hand-built<br/>for makers.</div>
      <div style={{fontSize:7,color:'#6B7280',marginTop:6,lineHeight:1.4}}>Tools that respect the craft. Materials that respect the earth.</div>
      <div style={{marginTop:10,display:'flex',gap:5}}><CTA label="Shop" /><CTA label="Story" dark/></div>
    </div>
    <img src={IMG.desk} alt="" style={{width:'48%',height:'100%',objectFit:'cover'}}/>
  </div>
);

// ── 4. Editorial Magazine ────────────────────────────────────────────
export const EditorialMagazine = () => (
  <div style={{background:'#fff',width:'100%',height:'100%',padding:'14px 16px',display:'flex',flexDirection:'column'}}>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid #0E0E23',paddingBottom:5}}>
      <div style={{fontSize:5,fontWeight:900,letterSpacing:2,textTransform:'uppercase'}}>Volume 04</div>
      <div style={{fontSize:5,letterSpacing:1}}>Spring 2026</div>
    </div>
    <div style={{flex:1,display:'grid',gridTemplateColumns:'1.4fr 1fr',gap:10,paddingTop:10}}>
      <div>
        <div style={{fontSize:20,fontWeight:900,color:'#0E0E23',lineHeight:0.95,letterSpacing:-1,fontFamily:'Georgia, serif'}}>The new<br/><em style={{fontWeight:400}}>quiet</em><br/>luxury.</div>
        <Line w="80%" mt={8} />
        <Line w="65%" mt={3} />
        <Line w="40%" mt={3} />
      </div>
      <img src={IMG.fashion} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
    </div>
  </div>
);

// ── 5. Floating Card Hero ────────────────────────────────────────────
export const FloatingCardHero = () => (
  <div style={{position:'relative',width:'100%',height:'100%'}}>
    <img src={IMG.city} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
    <div style={{position:'absolute',inset:0,background:'rgba(11,15,26,0.20)'}}/>
    <div style={{position:'absolute',left:14,bottom:14,right:14,background:'#fff',borderRadius:12,padding:12,boxShadow:'0 12px 32px rgba(0,0,0,0.25)'}}>
      <div style={{fontSize:6,color:'#8B5CF6',fontWeight:800,letterSpacing:1.2,textTransform:'uppercase'}}>Studio</div>
      <div style={{fontSize:13,fontWeight:900,color:'#0E0E23',marginTop:3,lineHeight:1.1}}>Design that breathes.</div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
        <div style={{fontSize:6,color:'#6B7280'}}>Available Q2 2026</div>
        <CTA label="Inquire" sz={7}/>
      </div>
    </div>
  </div>
);

// ── 6. Overlapping Hero ──────────────────────────────────────────────
export const OverlappingHero = () => (
  <div style={{display:'flex',width:'100%',height:'100%',background:'#F1ECE3',padding:14,gap:12,position:'relative'}}>
    <div style={{flex:1,zIndex:2,position:'relative'}}>
      <div style={{fontSize:6,color:'#0E0E23',fontWeight:900,letterSpacing:1.5,textTransform:'uppercase'}}>Atelier</div>
      <div style={{fontSize:22,fontWeight:900,color:'#0E0E23',marginTop:6,lineHeight:0.9,letterSpacing:-1.2}}>Tomorrow<br/>begins today.</div>
      <Line w="60%" color="#0E0E23" op={0.6} mt={8}/>
      <div style={{marginTop:10}}><CTA label="Explore" /></div>
    </div>
    <img src={IMG.forest} alt="" style={{width:'45%',height:'120%',objectFit:'cover',marginTop:-10,marginLeft:-30,position:'relative',zIndex:1,borderRadius:6}}/>
  </div>
);

// ── 7. Collage Hero ──────────────────────────────────────────────────
export const CollageHero = () => (
  <div style={{width:'100%',height:'100%',padding:10,background:'#F8FAFC',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gridTemplateRows:'1fr 1fr',gap:6}}>
    <img src={IMG.fashion} alt="" style={{width:'100%',height:'100%',objectFit:'cover',gridRow:'1 / span 2'}}/>
    <div style={{gridColumn:'2 / span 2',padding:'4px 8px',display:'flex',flexDirection:'column',justifyContent:'center'}}>
      <div style={{fontSize:14,fontWeight:900,color:'#0E0E23',lineHeight:1,letterSpacing:-0.5}}>Made with intention.</div>
      <div style={{fontSize:6,color:'#6B7280',marginTop:4}}>A collection of moments, objects, and craft.</div>
    </div>
    <img src={IMG.food} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
    <img src={IMG.nature} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
  </div>
);

// ── 8. Large Typography ──────────────────────────────────────────────
export const LargeTypography = () => (
  <div style={{background:'#0E0E23',width:'100%',height:'100%',display:'flex',flexDirection:'column',justifyContent:'center',padding:'14px 18px',color:'#fff'}}>
    <div style={{fontSize:36,fontWeight:900,lineHeight:0.85,letterSpacing:-2,fontFamily:'Georgia, serif'}}>BOLD.<br/><em style={{fontWeight:300}}>quiet.</em><br/>FOREVER.</div>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginTop:10}}>
      <div style={{fontSize:6,opacity:0.6,letterSpacing:1.5,textTransform:'uppercase'}}>Brand · Strategy · Type</div>
      <span style={{fontSize:9,fontWeight:700}}>→</span>
    </div>
  </div>
);

// ── 9. Offset / Asymmetric Hero ──────────────────────────────────────
export const OffsetAsymmetric = () => (
  <div style={{position:'relative',width:'100%',height:'100%',background:'#FCFAF6',overflow:'hidden'}}>
    <img src={IMG.brand} alt="" style={{position:'absolute',top:0,right:0,width:'62%',height:'82%',objectFit:'cover',borderBottomLeftRadius:6}}/>
    <div style={{position:'absolute',left:14,bottom:14,maxWidth:'56%'}}>
      <div style={{fontSize:6,color:'#8B5CF6',fontWeight:900,letterSpacing:1.5,textTransform:'uppercase'}}>Issue 12</div>
      <div style={{fontSize:16,fontWeight:900,color:'#0E0E23',marginTop:5,lineHeight:0.95}}>Less to see.<br/>More to feel.</div>
      <div style={{marginTop:8}}><CTA label="Begin" /></div>
    </div>
  </div>
);

// ── 10. Storytelling Big Type ────────────────────────────────────────
export const StorytellingBigType = () => (
  <div style={{width:'100%',height:'100%',background:'#fff',padding:14,display:'flex',flexDirection:'column'}}>
    <div style={{fontSize:6,letterSpacing:1.5,color:'#6B7280',fontWeight:800,textTransform:'uppercase'}}>A story</div>
    <div style={{flex:1,display:'flex',alignItems:'center'}}>
      <div style={{fontSize:14,fontFamily:'Georgia, serif',color:'#0E0E23',lineHeight:1.25,fontStyle:'italic'}}>
        “We built this for the people who notice the stitching. The grain of the table. The way the light falls at four.”
      </div>
    </div>
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      <img src={IMG.studio} alt="" style={{width:16,height:16,borderRadius:999,objectFit:'cover'}}/>
      <div>
        <div style={{fontSize:6,fontWeight:800,color:'#0E0E23'}}>Sarah K., Founder</div>
        <div style={{fontSize:5,color:'#9CA3AF'}}>Est. 2021</div>
      </div>
    </div>
  </div>
);

// ── 11. Brand Showcase ───────────────────────────────────────────────
export const BrandShowcase = () => (
  <div style={{width:'100%',height:'100%',background:'#FAF4EB',display:'flex',flexDirection:'column'}}>
    <div style={{flex:1.6,position:'relative'}}>
      <img src={IMG.brand} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
      <div style={{position:'absolute',left:14,bottom:14,color:'#fff'}}>
        <div style={{fontSize:18,fontWeight:900,lineHeight:0.95,letterSpacing:-0.5}}>Featured: Maya</div>
      </div>
    </div>
    <div style={{padding:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <div style={{fontSize:5,letterSpacing:1.2,fontWeight:800,color:'#0E0E23',textTransform:'uppercase'}}>Spring Collection · 24 pieces</div>
      <CTA label="View All" sz={6}/>
    </div>
  </div>
);

// ── 12. Video Hero ───────────────────────────────────────────────────
export const VideoHero = () => (
  <div style={{position:'relative',width:'100%',height:'100%'}}>
    <img src={IMG.city} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
    <div style={{position:'absolute',inset:0,background:'rgba(14,14,35,0.50)'}}/>
    <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',justifyContent:'center',alignItems:'center',color:'#fff'}}>
      <div style={{width:36,height:36,borderRadius:999,background:'rgba(255,255,255,0.18)',backdropFilter:'blur(6px)',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:8,border:'1px solid rgba(255,255,255,0.30)'}}>
        <span style={{fontSize:14,marginLeft:3}}>▶</span>
      </div>
      <div style={{fontSize:14,fontWeight:900,letterSpacing:-0.3}}>See it in motion</div>
      <div style={{fontSize:6,opacity:0.75,marginTop:3,letterSpacing:1.2,textTransform:'uppercase'}}>2 min watch · 4K</div>
    </div>
  </div>
);

// ── 13. Multi-Image Hero (3-up vertical) ─────────────────────────────
export const MultiImage = () => (
  <div style={{width:'100%',height:'100%',display:'grid',gridTemplateColumns:'1fr 1fr 1fr',background:'#000',gap:1}}>
    {[IMG.fashion, IMG.brand, IMG.studio].map((s,i)=>(
      <div key={i} style={{position:'relative'}}>
        <img src={s} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
        <div style={{position:'absolute',inset:0,background:i===1?'rgba(0,0,0,0.45)':'rgba(0,0,0,0)'}}/>
        {i===1 && <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'#fff',textAlign:'center',padding:6}}>
          <div style={{fontSize:11,fontWeight:900,lineHeight:1.05}}>Three<br/>collections.</div>
          <div style={{marginTop:6}}><CTA dark sz={6} label="Open" /></div>
        </div>}
      </div>
    ))}
  </div>
);

// ── 14. Layered (image + colored block + text overlap) ───────────────
export const LayeredHero = () => (
  <div style={{position:'relative',width:'100%',height:'100%',background:'#fff'}}>
    <img src={IMG.studio} alt="" style={{position:'absolute',top:8,right:8,width:'58%',height:'78%',objectFit:'cover',borderRadius:4}}/>
    <div style={{position:'absolute',top:'28%',left:8,width:'52%',height:'48%',background:'#F97316',borderRadius:4,zIndex:1}}/>
    <div style={{position:'absolute',top:'32%',left:14,zIndex:2,color:'#fff'}}>
      <div style={{fontSize:6,fontWeight:900,letterSpacing:1.5,textTransform:'uppercase',opacity:0.9}}>Capsule 02</div>
      <div style={{fontSize:14,fontWeight:900,lineHeight:0.95,marginTop:4,letterSpacing:-0.5}}>For the bold,<br/>the slow,<br/>the careful.</div>
      <div style={{marginTop:8}}><CTA dark sz={7}/></div>
    </div>
  </div>
);

// ── 15. Minimal Hero (lots of white, tiny content) ───────────────────
export const MinimalHero = () => (
  <div style={{width:'100%',height:'100%',background:'#fff',display:'flex',flexDirection:'column',justifyContent:'flex-end',padding:'14px 18px'}}>
    <div style={{flex:1,display:'flex',alignItems:'flex-start',justifyContent:'flex-start'}}>
      <div style={{width:60,height:1,background:'#0E0E23'}}/>
    </div>
    <div>
      <div style={{fontSize:24,fontWeight:300,color:'#0E0E23',lineHeight:1,letterSpacing:-1,fontFamily:'Georgia, serif'}}>
        <em>making.</em>
      </div>
      <div style={{fontSize:6,color:'#9CA3AF',marginTop:5,letterSpacing:1.5,textTransform:'uppercase'}}>est. 2021 · brooklyn ny</div>
    </div>
  </div>
);

// ── Legacy intro shapes (Iter 174) — used by introduce-marquee/banner/fluid
export const MarqueeBanner = () => (
  <div style={{ background:'#0F172A', width:'100%', height:'100%', display:'flex', flexDirection:'column', justifyContent:'center', overflow:'hidden' }}>
    <div style={{ whiteSpace:'nowrap', color:'#fff', fontSize:24, fontWeight:900, letterSpacing:-0.5, fontFamily:'Georgia, serif' }}>
      Indigenous Made · Authentic · Handcrafted · Indigenous Made · Authentic · Handcrafted
    </div>
    <div style={{ whiteSpace:'nowrap', color:'rgba(255,255,255,0.45)', fontSize:18, fontWeight:900, marginTop:4 }}>
      · Indigenous Made · Authentic · Handcrafted ·
    </div>
  </div>
);
export const AnnouncementBanner = () => (
  <div style={{ width:'100%', height:'100%', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', padding:'0 12px' }}>
    <div style={{ width:'100%', background:'linear-gradient(90deg, #8B5CF6, #6D28D9)', color:'#fff', padding:'10px 14px', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontSize:9, fontWeight:700, letterSpacing:0.4 }}>
      <span>✦</span><span>Free shipping on orders over $50</span><span>✦</span>
    </div>
  </div>
);
export const FluidEngine = () => (
  <div style={{ width:'100%', height:'100%', background:'#0E0E23', padding:10, display:'grid', gridTemplateColumns:'repeat(8, 1fr)', gridTemplateRows:'repeat(6, 1fr)', gap:3 }}>
    {Array.from({length:48}).map((_,i)=><div key={i} style={{ background:'rgba(255,255,255,0.04)', borderRadius:1 }} />)}
    <div style={{ position:'absolute', inset:14, display:'flex', flexDirection:'column', gap:5 }}>
      <div style={{ width:'72%', height:18, background:'#fff', borderRadius:2 }} />
      <div style={{ width:'55%', height:6, background:'rgba(255,255,255,0.5)', borderRadius:2 }} />
      <div style={{ width:54, height:14, background:'#8B5CF6', borderRadius:99, marginTop:8 }} />
    </div>
    <div style={{ position:'absolute', right:14, top:14, fontSize:6, color:'rgba(255,255,255,0.4)', fontWeight:700, letterSpacing:1.2, textTransform:'uppercase' }}>FLUID</div>
  </div>
);

// ── Iter 189c Phase B — new premium hero variants ──
const SplitHalfCover = () => (
  <div style={{ display:'flex', width:'100%', height:'100%' }}>
    <div style={{ width:'40%', background:'#0E0E23', color:'#fff', padding:14, display:'flex', flexDirection:'column', justifyContent:'center' }}>
      <div style={{ fontSize:6, color:'#A78BFA', fontWeight:800, letterSpacing:1.2, textTransform:'uppercase' }}>Collection · 2026</div>
      <div style={{ fontSize:15, fontWeight:900, lineHeight:1.05, marginTop:6 }}>Objects with weight.</div>
      <div style={{ fontSize:6, opacity:0.6, marginTop:5, lineHeight:1.4 }}>Every piece hand-finished.</div>
      <div style={{ display:'flex', gap:3, marginTop:8 }}>
        <div style={{ background:'#fff', color:'#0E0E23', fontSize:6, fontWeight:800, padding:'3px 7px', borderRadius:3 }}>Shop</div>
        <div style={{ border:'1px solid rgba(255,255,255,0.3)', color:'#fff', fontSize:6, fontWeight:800, padding:'3px 7px', borderRadius:3 }}>Story</div>
      </div>
    </div>
    <div style={{ width:'60%', position:'relative', background:'#F5F5F4' }}>
      <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=900&q=70" alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
    </div>
  </div>
);

const TestimonialHeroShape = () => (
  <div style={{ background:'#FAF6EE', width:'100%', height:'100%', padding:16, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center' }}>
    <div style={{ display:'flex', gap:2 }}>
      {[0,1,2,3,4].map(i => (
        <svg key={i} width={9} height={9} viewBox="0 0 12 12"><path d="M6 0l1.7 4 4.3.3-3.3 2.7 1 4.2L6 9l-3.7 2.2 1-4.2L0 4.3l4.3-.3z" fill="#F59E0B"/></svg>
      ))}
    </div>
    <div style={{ fontSize:12, fontStyle:'italic', color:'#0E0E23', fontWeight:800, marginTop:8, lineHeight:1.2, fontFamily:'Georgia,serif', maxWidth:'85%' }}>
      &ldquo;They rebuilt our brand — and doubled our revenue.&rdquo;
    </div>
    <div style={{ display:'flex', alignItems:'center', gap:5, marginTop:10 }}>
      <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&q=70" alt="" style={{ width:16, height:16, borderRadius:999, objectFit:'cover' }}/>
      <div style={{ textAlign:'left' }}>
        <div style={{ fontSize:6, fontWeight:900, color:'#0E0E23' }}>Amelia Ross</div>
        <div style={{ fontSize:5, color:'#94A3B8' }}>Kindred Studios</div>
      </div>
    </div>
  </div>
);

export const INTRO_SHAPE_BY_ID = {
  'centered-hero':              CenteredHero,
  'split-image-left':           SplitImageLeft,
  'split-image-right':          SplitImageRight,
  'editorial-magazine':         EditorialMagazine,
  'floating-card':              FloatingCardHero,
  'overlapping':                OverlappingHero,
  'collage':                    CollageHero,
  'large-typography':           LargeTypography,
  'offset-asymmetric':          OffsetAsymmetric,
  'storytelling-bigtype':       StorytellingBigType,
  'brand-showcase':             BrandShowcase,
  'video-hero':                 VideoHero,
  'multi-image':                MultiImage,
  'layered':                    LayeredHero,
  'minimal':                    MinimalHero,
  // Iter 174 — legacy intros
  'marquee-banner':             MarqueeBanner,
  'announcement-banner':        AnnouncementBanner,
  'fluid-engine':               FluidEngine,
  // Iter 189c Phase B — new premium hero variants (drawn miniature fallback;
  // hover reveals the drift-free live preview via LiveSectionPreview)
  'split-halfcover':            SplitHalfCover,
  'testimonial-hero':           TestimonialHeroShape,
};
